import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {z as zod} from "zod";

export enum AbacAction {
    create = "a27c5176-b045-45bd-b47b-3e0bee16d9c8",
    delete = "85b48273-b3db-4626-8ae0-3020cc82cdc5",
    read = "ca872c02-3921-482a-bb90-0609f5b67c5a",
    update = "e4b44e58-f84a-4753-beb8-9c6fbd98c61a",
}

export enum AbacConditionOperator {
    contains = "contains",
    endsWith = "endsWith",
    equals = "equals",
    exists = "exists",
    greaterThan = "greaterThan",
    greaterThanOrEqual = "greaterThanOrEqual",
    in = "in",
    /** Resource Country master ID is within the user's configured geography scope. */
    inGeographyCountries = "inGeographyCountries",
    /** Resource City master ID is within the user's configured geography scope or its descendants. */
    inGeographyCities = "inGeographyCities",
    /** Resource Pincode master ID is within the user's configured geography scope or its descendants. */
    inGeographyPincodes = "inGeographyPincodes",
    /** Resource State master ID is within the user's configured geography scope or its descendants. */
    inGeographyStates = "inGeographyStates",
    /** Resource Zone master ID is within the user's configured geography scope or its descendants. */
    inGeographyZones = "inGeographyZones",
    inHierarchyOf = "inHierarchyOf",
    inHierarchyOfRoles = "inHierarchyOfRoles",
    lessThan = "lessThan",
    lessThanOrEqual = "lessThanOrEqual",
    notContains = "notContains",
    notEquals = "notEquals",
    notExists = "notExists",
    notIn = "notIn",
    regex = "regex",
    startsWith = "startsWith",
}

export enum AbacLogicalOperator {
    and = "and",
    or = "or",
}

export enum AbacPolicyEffect {
    allow = "allow",
    deny = "deny",
}

export type AbacPolicyValue =
    | Array<AbacPolicyValue>
    | boolean
    | null
    | number
    | string
    | {
          [key: string]: AbacPolicyValue;
      };

export type AbacPolicy = {
    actions?: Array<AbacAction>;
    createdAt?: string;
    createdBy?: Uuid;
    description?: string;
    effect?: AbacPolicyEffect;
    enabled?: boolean;
    id?: Uuid;
    name?: string;
    priority?: number;
    resources?: {
        moduleIds?: Array<Uuid>;
        moduleTypes?: Array<Uuid>;
        resourceIds?: Array<Uuid>;
        resourceTypes?: Array<string>;
    };
    rules?: Array<{
        conditions?: Array<{
            attribute?: string;
            operator?: AbacConditionOperator;
            value?: AbacPolicyValue;
        }>;
        logicalOperator?: AbacLogicalOperator;
    }>;
    subject?: {
        userIds?: Array<Uuid>;
        userTemplateIds?: Array<Uuid>;
    };
    updatedAt?: string;
    updatedBy?: Uuid;
    workspaceId?: Uuid;
};

export const abacPolicyValueValidator: zod.ZodType<AbacPolicyValue> = zod.lazy(() =>
    zod.union([
        zod.array(abacPolicyValueValidator),
        zod.boolean(),
        zod.null(),
        zod.number(),
        zod.record(zod.string(), abacPolicyValueValidator),
        zod.string(),
    ]),
);

export const abacPolicyValidator: zod.ZodType<AbacPolicy> = zod.strictObject({
    actions: zod.enum(AbacAction).array().optional(),
    createdAt: zod.string().optional(),
    createdBy: uuidZodValidator.optional(),
    description: zod.string().optional(),
    effect: zod.enum(AbacPolicyEffect).optional(),
    enabled: zod.boolean().optional(),
    id: uuidZodValidator.optional(),
    name: zod.string().optional(),
    priority: zod.number().int().optional(),
    resources: zod
        .strictObject({
            moduleIds: uuidZodValidator.array().optional(),
            moduleTypes: uuidZodValidator.array().optional(),
            resourceIds: uuidZodValidator.array().optional(),
            resourceTypes: zod.string().array().optional(),
        })
        .optional(),
    rules: zod
        .strictObject({
            conditions: zod
                .strictObject({
                    attribute: zod.string().optional(),
                    operator: zod.enum(AbacConditionOperator).optional(),
                    value: abacPolicyValueValidator.optional(),
                })
                .array()
                .optional(),
            logicalOperator: zod.enum(AbacLogicalOperator).optional(),
        })
        .array()
        .optional(),
    subject: zod
        .strictObject({
            userIds: uuidZodValidator.array().optional(),
            userTemplateIds: uuidZodValidator.array().optional(),
        })
        .optional(),
    updatedAt: zod.string().optional(),
    updatedBy: uuidZodValidator.optional(),
    workspaceId: uuidZodValidator.optional(),
});
